import React from "react";
import { CHECKLIST_STATES } from "../../lib/checklistTemplate";

const stateColor = (s) => CHECKLIST_STATES.find((x) => x.value === s)?.color || "#6b7280";

export const FieldSubmissionView = ({ submission }) => {
  if (!submission) {
    return (
      <div className="bg-white border border-[#cfd7db] rounded-md p-6 text-sm text-[#3f4b52] text-center" data-testid="no-submission">
        No field submission yet. The fitter has not submitted this job.
      </div>
    );
  }
  const { checklist = {}, photos = [], client_signature, fitter_signature, submitted_by, submitted_at } = submission;

  return (
    <div className="space-y-6" data-testid="field-submission-view">
      <div className="bg-white border border-[#cfd7db] rounded-md p-5">
        <div className="font-display uppercase tracking-widest text-xs text-[#f28a16]">Field submission</div>
        <div className="mt-1 text-sm text-[#111619]">
          Submitted by <b>{submitted_by || "—"}</b> on {submitted_at ? new Date(submitted_at).toLocaleString("en-ZA") : "—"}
        </div>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <KV label="Fuel level" value={checklist.fuel_level} />
          <KV label="Windscreen" value={checklist.windscreen_type} />
          <KV label="Delivery" value={checklist.delivery_method} />
          <KV label="CS rating" value={checklist.rating_customer_service ? `${checklist.rating_customer_service}/5` : "—"} />
          <KV label="Premises" value={checklist.rating_premises ? `${checklist.rating_premises}/5` : "—"} />
          <KV label="Quality" value={checklist.rating_quality_of_work ? `${checklist.rating_quality_of_work}/5` : "—"} />
        </div>
      </div>

      {(checklist.items || []).length > 0 && (
        <div className="bg-white border border-[#cfd7db] rounded-md">
          <div className="px-4 py-2.5 bg-[#edf2f4] font-display uppercase tracking-widest text-xs text-[#3f4b52]">
            Inspection items
          </div>
          <ul className="divide-y divide-[#eef2f4]">
            {checklist.items.map((it) => (
              <li key={it.id} className="px-4 py-2.5 flex items-center justify-between gap-2 text-sm">
                <div className="min-w-0">
                  <div className="font-semibold text-[#111619] truncate">{it.label}</div>
                  {it.note && <div className="text-xs text-[#3f4b52]">{it.note}</div>}
                </div>
                <span
                  className="px-2 py-0.5 rounded-sm text-xs font-display uppercase tracking-widest text-white"
                  style={{ background: stateColor(it.state) }}
                >
                  {it.state}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {(checklist.damages || []).length > 0 && (
        <div className="bg-white border border-[#cfd7db] rounded-md p-4">
          <div className="font-display uppercase tracking-widest text-xs text-[#dc2626] mb-2">Damage markers</div>
          <ul className="text-sm text-[#3f4b52] list-decimal pl-5 space-y-1">
            {checklist.damages.map((d) => (
              <li key={d.id}>
                <span className="font-semibold text-[#111619]">[{d.view}]</span> {d.label || "(no description)"}
              </li>
            ))}
          </ul>
        </div>
      )}

      <PhotoGrid title="Existing damage" photos={photos.filter((p) => p.category === "damage")} />
      <PhotoGrid title="Before fitment" photos={photos.filter((p) => p.category === "before")} />
      <PhotoGrid title="After fitment" photos={photos.filter((p) => p.category === "after")} />

      {(client_signature || fitter_signature) && (
        <div className="bg-white border border-[#cfd7db] rounded-md p-4">
          <div className="font-display uppercase tracking-widest text-xs text-[#3f4b52] mb-3">Signatures</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {client_signature && (
              <div>
                <div className="text-xs uppercase text-[#3f4b52] mb-1">Client</div>
                <img src={client_signature} alt="Client signature" className="w-full border border-[#cfd7db] rounded-md bg-white" />
              </div>
            )}
            {fitter_signature && (
              <div>
                <div className="text-xs uppercase text-[#3f4b52] mb-1">Fitter</div>
                <img src={fitter_signature} alt="Fitter signature" className="w-full border border-[#cfd7db] rounded-md bg-white" />
              </div>
            )}
          </div>
        </div>
      )}

      {checklist.notes && (
        <div className="bg-white border border-[#cfd7db] rounded-md p-4">
          <div className="font-display uppercase tracking-widest text-xs text-[#3f4b52] mb-1">Fitter notes</div>
          <p className="text-sm text-[#111619] whitespace-pre-wrap">{checklist.notes}</p>
        </div>
      )}
    </div>
  );
};

const KV = ({ label, value }) => (
  <div>
    <div className="text-xs uppercase text-[#3f4b52]">{label}</div>
    <div className="font-semibold text-[#111619]">{value || "—"}</div>
  </div>
);

const PhotoGrid = ({ title, photos }) => {
  if (!photos.length) return null;
  return (
    <div className="bg-white border border-[#cfd7db] rounded-md p-4">
      <div className="font-display uppercase tracking-widest text-xs text-[#3f4b52] mb-3">{title} ({photos.length})</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {photos.map((p) => (
          <a key={p.id} href={p.data_url} target="_blank" rel="noopener noreferrer" className="aspect-square block border border-[#cfd7db] rounded-md overflow-hidden bg-white">
            <img src={p.data_url} alt="" className="w-full h-full object-cover" />
          </a>
        ))}
      </div>
    </div>
  );
};

export default FieldSubmissionView;
